import {
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  Column,
  ColumnRowGroupChangedEvent,
  ExcelExportParams,
  FilterChangedEvent,
  GridReadyEvent,
  IFilterOptionDef,
  RowDataTransaction,
  RowSelectionOptions,
  SelectionColumnDef,
  SortChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import constate from 'constate';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_MAX_ROW_DATA_LENGTH_TO_HIDE_FILTERS } from 'core/constants';
import { CellRendererForAgGrid } from 'core/elements';
import { AgGridFilterOption, TableLayoutType } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import {
  useAgGridExcelExport,
  useAgGridFilter,
  useAgGridLayout,
  useAgGridRowDragging,
} from 'core/hooks';
import {
  AgGridCellValueChangedResult,
  AgGridDataRow,
  AgGridRowsReorderResponse,
  AgGridTransactionOptions,
} from 'core/types';
import {
  applyTrimmedTextFilterModel,
  dateFilterComparator,
  defaultShouldRowBeUpdated,
  getCustomDateFilter,
  ICustomGridOptionsParams,
  scrollGridToTop,
  updateFloatingFiltersVisibility,
  useGetGridOptions,
} from 'core/utils';

import { useAgGridCellUpdateContext } from './agGridCellUpdate';
import { AgGridTableProvider, useAgGridTableContext } from './agGridTable';

interface IProps
  extends Omit<
    ICustomGridOptionsParams,
    | 'onCellValueChanged'
    | 'onCellEditingStarted'
    | 'onCellEditingStopped'
    | 'getRowId'
    | 'isRowSelectable'
  > {
  tableId: string;
  entityType: string;
  columnDefs: ColDef[];
  getRowId?: (data: AgGridDataRow) => string;
  data?: AgGridDataRow[];
  isDataLoading?: boolean;
  loadingPercentage?: number;
  dataCurrentPage?: number;
  onRowsReorder?: (diffRows: AgGridDataRow[]) => AgGridRowsReorderResponse;
  onCellEditingStarted?: (agGrid: AgGridReact, event: CellEditingStartedEvent) => void;
  onCellEditingStopped?: (agGrid: AgGridReact, event: CellEditingStoppedEvent) => void;
  onCellValueChanged?: (
    agGrid: AgGridReact,
    event: CellValueChangedEvent
  ) => Promise<AgGridCellValueChangedResult> | AgGridCellValueChangedResult;
  hasRowReordering?: boolean;
  layoutType?: TableLayoutType;
  hasLayoutActions?: boolean;
  hasManageFilters?: boolean;
  hasQuickSearch?: boolean;
  hasTopActions?: boolean;
  hasGroupByEnabled?: boolean;
  hasDefaultStatusBar?: boolean;
  hasRowSelection?: boolean;
  selectionMode?: 'multiRow' | 'singleRow';
  isRowSelectable?: (params: any) => boolean;
  headerCheckbox?: boolean;
  enableClickSelection?: boolean;
  rowBuffer?: number;
  excelExportParams?: Partial<ExcelExportParams>;
  shouldRowBeUpdated?: (newRow: AgGridDataRow, existingRow: AgGridDataRow) => boolean;
}

const cellStyle = {
  alignContent: 'center',
  lineHeight: '1.2em',
};

const baseDefaultColDef = {
  sortable: true,
  minWidth: 120,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  resizable: true,
  width: null,
  pinned: null,
  hide: null,
  filterParams: {
    buttons: ['reset', 'apply'],
    closeOnApply: true,
    closeOnReset: true,
    inRangeInclusive: true,
  },
  enableRowGroup: true,
  cellRenderer: CellRendererForAgGrid,
  cellStyle,
};

const getSupportedFilterOptions = (filter: string): (string | IFilterOptionDef)[] => {
  switch (filter) {
    case 'agDateColumnFilter':
      return [
        AgGridFilterOption.EQUALS,
        AgGridFilterOption.NOT_EQUAL,
        AgGridFilterOption.LESS_THAN,
        AgGridFilterOption.GREATER_THAN,
        AgGridFilterOption.IN_RANGE,
        AgGridFilterOption.BLANK,
        AgGridFilterOption.NOT_BLANK,
        ...getCustomDateFilter(),
      ];
    default:
      return undefined;
  }
};

const defaultStatusBar = {
  statusPanels: [
    {
      statusPanel: 'agTotalRowCountComponent',
      align: 'right',
      valueFormatter: NumberHelper.formatInteger,
    },
    {
      statusPanel: 'agFilteredRowCountComponent',
      align: 'right',
      valueFormatter: NumberHelper.formatInteger,
    },
  ],
};

const useAgGridClientTable = ({
  tableId,
  entityType,
  columnDefs,
  getRowId,
  data,
  isDataLoading,
  loadingPercentage,
  dataCurrentPage,
  statusBar: customStatusBar,
  defaultColDef: customDefaultColDef,
  selectionColumnDef: customSelectionColumnDef,
  onRowsReorder,
  onCellEditingStarted,
  onCellEditingStopped,
  onCellValueChanged,
  hasRowReordering = false,
  layoutType,
  hasLayoutActions = false,
  hasManageFilters = false,
  hasQuickSearch = true,
  hasTopActions = true,
  hasGroupByEnabled = true,
  initialState: initialStateProp,
  hasDefaultStatusBar = true,
  hasRowSelection = true,
  selectionMode = 'multiRow',
  isRowSelectable = null,
  headerCheckbox = false,
  enableClickSelection = false,
  rowBuffer,
  excelExportParams,
  shouldRowBeUpdated = defaultShouldRowBeUpdated,
  ...rest
}: IProps) => {
  const {
    gridRef,
    gridApi,
    getCurrentRows,
    selectedRows,
    selectedRowsIds,
    onSelectionChanged,
    deselectAllRows,
    onRowsUpdated,
  } = useAgGridTableContext();

  const [rowGroupColumns, setRowGroupColumns] = useState<Column[]>([]);

  const [rows, setRows] = useState<AgGridDataRow[]>([]);
  const rowsRef = useRef<AgGridDataRow[]>([]);

  const previousFloatingFilterState = useRef<boolean | null>(null);

  const { setUpdating } = useAgGridCellUpdateContext();

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const isDataLoadCompleted = loadingPercentage === 100;
  const isLoadingFirstPage = isDataLoading || !dataCurrentPage;

  const { t } = useTranslation();

  const columnDefsWithDefaultFilterOptions = useMemo(() => {
    // A column group's columns get theirs too: the group itself has no filter.
    const withFilterOptions = (columns: ColDef[]): ColDef[] =>
      columns.filter(Boolean).map((column) => {
        if ('children' in column) {
          return { ...column, children: withFilterOptions((column as ColGroupDef).children) };
        }

        const { filter, filterParams, ...rest } = column;

        return {
          ...rest,
          filter,
          filterParams: {
            filterOptions: getSupportedFilterOptions(filter),
            // Custom React filter components render their own Reset/Apply buttons.
            // Suppress AG-Grid's native action buttons (inherited from defaultColDef)
            // so they aren't injected on top, producing a duplicate apply panel.
            ...(typeof filter === 'function' && { buttons: [] }),
            ...(filter === 'agTextColumnFilter' && { trimInput: true }),
            ...(filter === 'agDateColumnFilter' && { comparator: dateFilterComparator }),
            ...filterParams,
          },
        };
      });

    return withFilterOptions(columnDefs);
  }, [columnDefs]);

  const { defaultCsvExportParams, defaultExcelExportParams } = useAgGridExcelExport({
    gridApi,
    excelExportParams,
  });

  const { initialState, saveFilterState, defaultFilterModel } = useAgGridFilter({
    layoutType,
    initialState: initialStateProp,
    hasManageFilters,
    columnDefs,
  });

  const {
    layoutChanged,
    onFilterChanged: agGridLayoutOnFilterChanged,
    onSaveLayout,
    resetMenuItems,
  } = useAgGridLayout({
    gridApi,
    rows,
    columnDefs: columnDefsWithDefaultFilterOptions,
    hasLayoutActions,
    layoutType,
    initialState,
    isDataLoadCompleted,
    defaultFilterModel,
  });

  useEffect(() => {
    const onRowDataChanged = () => {
      const currentRows = getCurrentRows();

      setRows(currentRows);

      const showFloatingFilter = currentRows?.length > DEFAULT_MAX_ROW_DATA_LENGTH_TO_HIDE_FILTERS;

      if (previousFloatingFilterState.current === showFloatingFilter) {
        return;
      }

      previousFloatingFilterState.current = showFloatingFilter;

      // Save current column state (order, width, pinned, etc.) before updating defs
      const columnState = gridApi?.getColumnState();

      const updatedColumnDefs = updateFloatingFiltersVisibility(
        columnDefsWithDefaultFilterOptions,
        showFloatingFilter
      );

      gridApi?.setGridOption('columnDefs', updatedColumnDefs);

      // Re-apply column state to restore user's column order
      if (columnState) {
        gridApi?.applyColumnState({ state: columnState, applyOrder: true });
      }
    };

    gridApi?.addEventListener('rowDataUpdated', onRowDataChanged);

    return () => {
      !gridApi?.isDestroyed() && gridApi?.removeEventListener('rowDataUpdated', onRowDataChanged);
      previousFloatingFilterState.current = null;
    };
  }, [gridApi, getCurrentRows, columnDefsWithDefaultFilterOptions]);

  useEffect(() => {
    if (rows.length === 0 && isLoadingFirstPage) {
      gridApi?.setGridOption('loading', true);

      return;
    }

    gridApi?.setGridOption('loading', false);
  }, [isLoadingFirstPage, gridApi, rows]);

  const defaultColDef = useMemo(
    () => ({ ...baseDefaultColDef, ...customDefaultColDef }),
    [customDefaultColDef]
  );

  const selectionColumnDef: SelectionColumnDef = useMemo(
    () => ({
      minWidth: 40,
      width: 40,
      maxWidth: 40,
      suppressColumnsToolPanel: true,
      suppressMovable: true,
      lockPosition: true,
      headerClass: 'paddingSmall center',
      cellClass: 'paddingSmall center noDecorationCell',
      pinned: 'left',
      lockPinned: true,
      ...customSelectionColumnDef,
    }),
    [customSelectionColumnDef]
  );

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerValueGetter: ({ colDef: { headerName } }) => t('headerGroupedColumn', { headerName }),
      pinned: 'left',
      autoHeight: true,
      minWidth: 200,
      maxWidth: 400,
      filter: 'agGroupColumnFilter',
    }),
    [t]
  );

  const statusBar = useMemo(() => {
    const defaultStatusPanels = [];
    if (hasDefaultStatusBar && defaultStatusBar?.statusPanels) {
      defaultStatusPanels.push(...defaultStatusBar.statusPanels);
    }

    return {
      statusPanels: [...(customStatusBar?.statusPanels ?? []), ...defaultStatusPanels],
    };
  }, [customStatusBar?.statusPanels, hasDefaultStatusBar]);

  const handleUpdateRows = useCallback(
    (rowDataTransaction: RowDataTransaction, options?: AgGridTransactionOptions) => {
      const shouldRunTransaction =
        rowDataTransaction?.add?.length ||
        rowDataTransaction?.remove?.length ||
        rowDataTransaction?.update?.length ||
        !!options;

      if (!shouldRunTransaction || !gridApi) {
        return;
      }

      const resultTransaction = gridApi?.applyTransaction(rowDataTransaction);

      let lastAddedRowIndex = null;

      resultTransaction?.add?.forEach((row) => (lastAddedRowIndex = row.rowIndex));

      setRows(getCurrentRows());

      onRowsUpdated({
        goToRowIndex: rowDataTransaction.addIndex || lastAddedRowIndex,
        ...options,
      });
    },
    [getCurrentRows, gridApi, onRowsUpdated]
  );

  const handleReplaceRow = useCallback(
    (
      oldRow: AgGridDataRow,
      newRow: AgGridDataRow,
      options?: AgGridTransactionOptions,
      addIndex?: number
    ) => {
      const oldRowNodeIndex =
        addIndex !== undefined ? addIndex : gridApi?.getRowNode(oldRow?.id.toString())?.rowIndex;

      gridApi?.applyTransaction({
        remove: [oldRow],
        add: [newRow],
        addIndex: oldRowNodeIndex,
      });

      setRows(getCurrentRows());

      onRowsUpdated(options);
    },
    [getCurrentRows, gridApi, onRowsUpdated]
  );

  const handleReplaceRows = useCallback(
    (
      oldRows: AgGridDataRow[],
      newRows: AgGridDataRow[],
      options?: AgGridTransactionOptions,
      addIndex?: number
    ) => {
      if (!gridApi) {
        if (newRows?.length) {
          setRows(newRows);
          rowsRef.current = newRows;
        }

        return;
      }

      if (!newRows?.length && !oldRows?.length) {
        return;
      }

      const firstOldNode = gridApi?.getRowNode(oldRows?.[0]?.id?.toString());

      const oldRowNodeIndexStart =
        addIndex !== undefined ? addIndex : firstOldNode?.rowIndex || firstOldNode?.sourceRowIndex;

      gridApi?.applyTransaction({
        remove: oldRows,
      });

      gridApi?.applyTransaction({
        add: newRows,
        addIndex: oldRowNodeIndexStart,
      });

      setRows(getCurrentRows());

      onRowsUpdated(options);
    },
    [getCurrentRows, gridApi, onRowsUpdated]
  );

  const onGridReady = useCallback(
    ({ api }: GridReadyEvent) => {
      if (rowsRef.current.length > 0 && rows.length === 0) {
        api.applyTransaction({
          add: rowsRef.current,
        });
      }
    },
    [rows.length]
  );

  // Keep a ref to the latest getRowId so the diff effect doesn't re-fire on every parent
  // render when callers pass an inline getRowId (a fresh reference each render). Effect
  // depends only on `data` / handlers below; the ref always returns the latest function.
  const getRowIdRef = useRef(getRowId);
  getRowIdRef.current = getRowId;

  useEffect(() => {
    const existingRows = getCurrentRows();

    // Key rows by the same id the grid uses (getRowId prop, falling back to row.id).
    // Using row.id alone breaks for data shapes where multiple rows share an id but
    // are made unique via getRowId (e.g. inventory rows: many reels share a location id).
    const resolveRowId = (row: AgGridDataRow) =>
      getRowIdRef.current ? getRowIdRef.current(row) : row?.id?.toString();

    const existingRowMapped = new Map(existingRows.map((row) => [resolveRowId(row), row]));

    const dataRowIds = new Set<string>();

    const oldRows = [];
    const updateRows = [];

    data.forEach((row) => {
      const rowId = resolveRowId(row);
      dataRowIds.add(rowId);

      const existingRow = existingRowMapped.get(rowId);

      if (!existingRow) {
        updateRows.push(row);

        return;
      }

      if (shouldRowBeUpdated(row, existingRow)) {
        oldRows.push(existingRow);
        updateRows.push(row);
      }
    });

    const firstUpdateRowIndex = data.indexOf(updateRows?.[0]);

    const removeRows = existingRows.filter((row) => !dataRowIds.has(resolveRowId(row))) || [];
    oldRows.push(...removeRows);

    const isOnlyAddingNewRows = oldRows.length === 0;

    handleReplaceRows(
      oldRows,
      updateRows,
      {
        goToRowIndex: 0,
        redrawRows: !isOnlyAddingNewRows,
        refreshCells: !isOnlyAddingNewRows,
        skipDeselect: isOnlyAddingNewRows,
      },
      firstUpdateRowIndex
    );
  }, [data, getCurrentRows, handleReplaceRows, shouldRowBeUpdated]);

  const isSortFilterOrGroupByApplied = useCallback(({ api }) => {
    const isSortedEnabled = api.getColumns().some((column) => column.getSort());
    const isFilterActive = api.getColumns().some((column: Column) => column.isFilterActive());
    const isGroupByApplied = api.getRowGroupColumns().length > 0;

    return isSortedEnabled || isFilterActive || isGroupByApplied;
  }, []);

  // When line column has no reodering, it should be aligned to center.
  // Used onSortChanged, onCellEditingStarted and onCellEditingStopped to update the alignment.
  const applyCellAlignmentOnLineColumn = useCallback(
    (center = false) => {
      if (!hasRowReordering || !gridApi) {
        return;
      }

      const lineColumn = gridApi.getColumn('line');

      if (!lineColumn) {
        return;
      }

      const colDef = lineColumn.getColDef();

      colDef.cellStyle = {
        ...colDef.cellStyle,
        alignItems: center ? 'center' : 'flex-start',
      };

      gridApi.refreshCells({ columns: ['line'], force: true });
    },
    [gridApi, hasRowReordering]
  );

  const onSortChanged = useCallback(
    ({ api }: SortChangedEvent) => {
      const sortFilterOrGroupByApplied = isSortFilterOrGroupByApplied({ api });

      applyCellAlignmentOnLineColumn(sortFilterOrGroupByApplied);

      if (hasRowReordering) {
        api?.setGridOption('suppressRowDrag', sortFilterOrGroupByApplied);
      }
    },
    [applyCellAlignmentOnLineColumn, hasRowReordering, isSortFilterOrGroupByApplied]
  );

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      if (applyTrimmedTextFilterModel(event.api)) {
        return;
      }

      applyCellAlignmentOnLineColumn(isSortFilterOrGroupByApplied(event));

      deselectAllRows();

      saveFilterState(event);

      agGridLayoutOnFilterChanged && agGridLayoutOnFilterChanged(event);

      scrollGridToTop(event.api);
    },
    [
      agGridLayoutOnFilterChanged,
      applyCellAlignmentOnLineColumn,
      deselectAllRows,
      isSortFilterOrGroupByApplied,
      saveFilterState,
    ]
  );

  const { onRowDragEnter, onRowDragMove, onRowDragEnd } = useAgGridRowDragging({
    gridApi,
    rows,
    onRowsReorder,
    handleUpdateRows,
    handleReplaceRows,
  });

  const handleOnColumnRowGroupChanged = useCallback(
    ({ api }: ColumnRowGroupChangedEvent) => {
      const newRowGroupColumns = api.getRowGroupColumns();

      setRowGroupColumns([...newRowGroupColumns]);

      applyCellAlignmentOnLineColumn(isSortFilterOrGroupByApplied({ api }));
    },
    [applyCellAlignmentOnLineColumn, isSortFilterOrGroupByApplied]
  );

  const handleOnCellEditingStarted = useCallback(
    (event: CellEditingStartedEvent) => {
      if (hasRowReordering) {
        gridApi?.setGridOption('suppressRowDrag', true);
      }

      applyCellAlignmentOnLineColumn(true);

      onCellEditingStarted && onCellEditingStarted(gridRef.current, event);
    },
    [applyCellAlignmentOnLineColumn, gridApi, gridRef, hasRowReordering, onCellEditingStarted]
  );

  const handleOnCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent) => {
      const sortFilterOrGroupByApplied = isSortFilterOrGroupByApplied({ api: event?.api });

      if (hasRowReordering) {
        event.api?.setGridOption('suppressRowDrag', sortFilterOrGroupByApplied);
      }

      applyCellAlignmentOnLineColumn(sortFilterOrGroupByApplied);

      onCellEditingStopped && onCellEditingStopped(gridRef.current, event);
    },
    [
      isSortFilterOrGroupByApplied,
      hasRowReordering,
      applyCellAlignmentOnLineColumn,
      onCellEditingStopped,
      gridRef,
    ]
  );

  const handleOnCellValueChanged = useCallback(
    async (event: CellValueChangedEvent) => {
      if (!onCellValueChanged) {
        return;
      }

      try {
        setUpdating(event.node.id, event.colDef.colId, true);

        const { updatedRow, removedRow, options } =
          onCellValueChanged && (await onCellValueChanged(gridRef.current, event));

        updatedRow &&
          handleUpdateRows(
            { update: [{ ...event.data, ...updatedRow }] },
            { refreshCells: true, redrawRows: true, ...options }
          );

        removedRow &&
          handleUpdateRows(
            { remove: [removedRow] },
            { refreshCells: true, redrawRows: true, ...options }
          );
      } catch (error) {
        const revertedRow = {
          ...event.data,
          [event.colDef.field]: event.oldValue,
        };

        handleUpdateRows({ update: [revertedRow] }, { refreshCells: true, redrawRows: true });
      } finally {
        setUpdating(event.node.id, event.colDef.colId, false);
      }
    },
    [onCellValueChanged, setUpdating, gridRef, handleUpdateRows]
  );

  const rowSelection: RowSelectionOptions<unknown, unknown> = useMemo(
    () => ({
      headerCheckbox,
      checkboxes: hasRowSelection,
      selectAll: 'filtered',
      mode: selectionMode,
      enableClickSelection,
      groupSelects: 'filteredDescendants',
      ...(isRowSelectable && { isRowSelectable }),
    }),
    [headerCheckbox, hasRowSelection, selectionMode, enableClickSelection, isRowSelectable]
  );

  const gridOptions = useGetGridOptions({
    columnDefs: columnDefsWithDefaultFilterOptions,
    defaultColDef,
    autoGroupColumnDef,
    rowBuffer,
    getRowId: useCallback(
      ({ data }) => (getRowId ? getRowId(data) : data?.id?.toString()),
      [getRowId]
    ),
    onGridReady,
    onSelectionChanged,
    onSortChanged,
    onFilterChanged,
    statusBar,
    hasRowReordering,
    onColumnRowGroupChanged: handleOnColumnRowGroupChanged,
    onRowDragEnter,
    onRowDragMove,
    onRowDragEnd,
    onCellEditingStarted: handleOnCellEditingStarted,
    onCellEditingStopped: handleOnCellEditingStopped,
    onCellValueChanged: handleOnCellValueChanged,
    localeText: {
      // "Family" counts as "Families", as the server-side footer spells it.
      totalRows: t('labelTotalWithType', {
        entityType: t(`label${entityType.replace(/([^aeiou])y$/, '$1ie')}s`),
      }),
      loadingError: t('errorTitleDataLoading'),
    },
    initialState,
    rowSelection,
    selectionColumnDef,
    defaultCsvExportParams,
    defaultExcelExportParams,
    ...rest,
  });

  return {
    tableId,
    entityType,
    gridRef,
    gridApi,
    gridOptions,
    rows,
    selectedRows,
    selectedRowsIds,
    updateRows: handleUpdateRows,
    replaceRow: handleReplaceRow,
    loadingPercentage,
    isDataLoading,
    isDataLoadCompleted,
    layoutType,
    hasLayoutActions,
    hasManageFilters,
    layoutChanged,
    onSaveLayout,
    resetMenuItems,
    rowGroupColumns,
    hasQuickSearch,
    hasTopActions,
    hasGroupByEnabled,
    deselectAllRows,
  };
};

export const [AgGridClientTableProviderWithoutCommonProvider, useAgGridClientTableContext] =
  constate(useAgGridClientTable);

export const AgGridClientTableProvider = ({
  children,
  ...props
}: IProps & { children: ReactNode }) => {
  return (
    <AgGridTableProvider {...props}>
      <AgGridClientTableProviderWithoutCommonProvider {...props}>
        {children}
      </AgGridClientTableProviderWithoutCommonProvider>
    </AgGridTableProvider>
  );
};
